<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLanguageStore } from '../stores/language'

const isOpen = ref(false)
const route = useRoute()
const router = useRouter()
const languageStore = useLanguageStore()
const languageOptions = languageStore.languageOptions
const selectedLanguage = computed(() => languageStore.currentLanguage)

const selectLanguage = (lang: string) => {
  languageStore.setLanguage(lang as 'tr' | 'en')
  isOpen.value = false

  const targetPath = languageStore.localizePath(route.path)
  if (targetPath !== route.path) {
    router.push(targetPath)
  }
}
</script>

<template>
  <div class="lang-fab-container" :class="{ 'is-open': isOpen }" @mouseenter="isOpen = true" @mouseleave="isOpen = false">
    <div class="lang-options">
      <button
        v-for="option in languageOptions"
        :key="option.code"
        class="lang-btn"
        :class="{ active: selectedLanguage === option.code }"
        @click="selectLanguage(option.code)"
      >
        {{ option.label }}
      </button>
    </div>
    <button
      class="fab-main"
      @click="isOpen = !isOpen"
      :aria-expanded="isOpen"
      aria-label="Dili Değiştir / Change Language"
    >
      <i class="pi pi-globe"></i>
      <span class="current-lang">{{ selectedLanguage.toUpperCase() }}</span>
    </button>
  </div>
</template>

<style scoped>
.lang-fab-container {
  position: fixed;
  bottom: 2rem;
  left: 2rem; /* Usually FAB goes right, but right might have live chat. Let's put it left or right. Left is safe. Let's use left */
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.lang-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.lang-fab-container.is-open .lang-options {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: all;
}

.lang-btn {
  background: var(--background-white, #fff);
  border: 1px solid rgba(var(--primary-rgb), 0.1);
  color: var(--text-secondary);
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  white-space: nowrap;
}

.lang-btn:hover {
  transform: scale(1.05);
  color: var(--accent-color);
}

.lang-btn.active {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%);
  color: white;
  border: none;
}

.fab-main {
  background: var(--background-white, #fff);
  border: 1px solid rgba(var(--primary-rgb), 0.1);
  border-radius: 999px;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.1rem;
}

.fab-main:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  color: var(--accent-color);
}

.fab-main i {
  font-size: 1.3rem;
}

@media (max-width: 768px) {
  .lang-fab-container {
    bottom: 1.5rem;
    left: 1.5rem;
  }
}
</style>
